import { useEffect, useRef, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { toast } from 'react-toastify';

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 5000;

/**
 * Hook for real-time livestream status updates via WebSocket
 * @param {string|number} livestreamId - The livestream ID to monitor
 * @param {Function} onStatusUpdate - Callback when status changes
 * @param {boolean} enabled - Whether to enable real-time updates
 */
export const useLivestreamStatusUpdates = (livestreamId, onStatusUpdate, enabled = true) => {
  const stompClientRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const endpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'http://localhost:8080/ws';

  const connectWebSocket = useCallback(() => {
    if (!enabled || !livestreamId || stompClientRef.current?.connected) {
      return;
    }

    const socket = new SockJS(endpoint);
    const stompClient = new Client({
      webSocketFactory: () => socket,
      debug: (str) => {
        if (process.env.NODE_ENV === 'development') {
        }
      },

      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        reconnectAttemptsRef.current = 0;

        // Subscribe to livestream status updates
        stompClient.subscribe(`/topic/livestream/${livestreamId}/status`, (message) => {
          try {
            const statusUpdate = JSON.parse(message.body);

            // Call the callback with the status update
            if (onStatusUpdate) {
              onStatusUpdate(statusUpdate);
            }

            // Show toast for important status changes
            if (statusUpdate.status === 'IN_PROGRESS' && statusUpdate.previousStatus === 'SCHEDULED') {
              toast.info('Livestream has started!', { autoClose: 5000 });
            } else if (statusUpdate.status === 'COMPLETED' && statusUpdate.previousStatus === 'IN_PROGRESS') {
              toast.info('Livestream has ended', { autoClose: 5000 });
            } else if (statusUpdate.status === 'CANCELLED') {
              toast.warning('Livestream has been cancelled', { autoClose: 5000 });
            }
          } catch (error) {
          }
        });

        // Subscribe to livestream viewer updates
        stompClient.subscribe(`/topic/livestream/${livestreamId}/viewers`, (message) => {
          try {
            const viewerUpdate = JSON.parse(message.body);

            // Call the callback with viewer update
            if (onStatusUpdate) {
              onStatusUpdate({
                type: 'viewer_update',
                ...viewerUpdate
              });
            }
          } catch (error) {
          }
        });

      },
      onStompError: (frame) => {
        handleReconnect();
      },
      onWebSocketError: (error) => {
        handleReconnect();
      },
      onDisconnect: () => {
        handleReconnect();
      }
    });

    stompClientRef.current = stompClient;
    stompClient.activate();
  }, [livestreamId, enabled, onStatusUpdate, endpoint]);

  const handleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    reconnectAttemptsRef.current++;

    retryTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, RECONNECT_DELAY);
  }, [connectWebSocket]);

  const disconnect = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
  }, []);

  // Connect on mount and when dependencies change
  useEffect(() => {
    if (enabled && livestreamId) {
      connectWebSocket();
    }

    return () => {
      disconnect();
    };
  }, [livestreamId, enabled, connectWebSocket, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected: stompClientRef.current?.connected || false,
    disconnect
  };
};

export default useLivestreamStatusUpdates;
